import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDataProvider, useNotify, useGetOne } from 'react-admin';
import {
  Box, Card, CardContent, Grid, Avatar, Typography, Chip, Button,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Divider, IconButton, Tooltip, Checkbox, FormControlLabel,
  Paper, LinearProgress, alpha, Stack,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import CasinoIcon from '@mui/icons-material/Casino';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PaymentsIcon from '@mui/icons-material/Payments';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PaymentIcon from '@mui/icons-material/Payment';
import TuneIcon from '@mui/icons-material/Tune';
import ListAltIcon from '@mui/icons-material/ListAlt';

const fmt = (n) => `৳ ${Number(n || 0).toLocaleString('en-BD', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// ---------------------------------------------------------------------------
// ডিজাইন টোকেন — গ্রিড কার্ড ইউজারের জন্য একটাই consistent সেট
// ---------------------------------------------------------------------------
const CARD_BG = '#141c30';
const CARD_BORDER = 'rgba(255,255,255,0.08)';
const ROW_BORDER = 'rgba(255,255,255,0.07)';

const CARD_SX = {
  height: '100%',
  width: '100%',
  borderRadius: 3,
  bgcolor: CARD_BG,
  border: `1px solid ${CARD_BORDER}`,
  boxShadow: 'none',
};

// আইকন-ব্যাজ সহ স্ট্যাট কার্ড — উপরের row (Current Balance, Total Deposit ইত্যাদি)
const StatCard = ({ label, value, color, icon: Icon, sub }) => (
  <Card sx={{ ...CARD_SX, position: 'relative', overflow: 'hidden', minWidth: 0 }}>
    <Box sx={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: color }} />
    <CardContent sx={{ pl: 2.5 }}>
      <Box display="flex" alignItems="center" gap={1.25} mb={0.75}>
        {Icon && (
          <Box
            sx={{
              width: 30, height: 30, borderRadius: '9px',
              bgcolor: alpha(color, 0.16), color,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 17 }} />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>{label}</Typography>
      </Box>
      <Typography variant="h6" sx={{ color: color || 'text.primary', fontWeight: 700, letterSpacing: '-0.01em' }}>
        {value}
      </Typography>
      {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
    </CardContent>
  </Card>
);

// প্রতিটা section কার্ডের uniform icon+label header
const SectionHeader = ({ icon: Icon, label, color, action }) => (
  <Box display="flex" alignItems="center" gap={1.1} mb={1.5}>
    <Box
      sx={{
        width: 26, height: 26, borderRadius: '8px',
        bgcolor: alpha(color, 0.16), color,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>{label}</Typography>
    {action}
  </Box>
);

// section কার্ডের ভেতরে key–value সারি; পুরনো <Table> এর বদলে ব্যবহার করলে reference এর মতো
// সরু divider সহ সারি পাওয়া যায় (dense MUI Table এর ডিফল্ট border এর চেয়ে হালকা দেখায়)
const InfoRow = ({ label, value, valueColor, last }) => (
  <Box
    display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}
    sx={{ py: 0.85, borderBottom: last ? 'none' : `1px solid ${ROW_BORDER}` }}
  >
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, color: valueColor || 'text.primary', textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const StatusChip = ({ status }) => {
  const map = { Approved: 'success', Pending: 'warning', Rejected: 'error', Solved: 'success', Open: 'warning' };
  return <Chip size="small" label={status} color={map[status] || 'default'} />;
};

const WinLossChip = ({ status }) => (
  <Chip size="small" label={status} color={status === 'Won' ? 'success' : 'error'} />
);

// ---------------------------------------------------------------------------
// RelatedTable
// নতুন (Withdraw Check এর জন্য) ৩টা ঐচ্ছিক prop যোগ হয়েছে:
//   rowFilter  — ডেটা আসার পর client-side এ কোন সারিগুলো দেখাবে সেটা ঠিক করে
//   emptyText  — কোনো সারি না থাকলে কী লেখা দেখাবে
//   perPage    — একবারে কতগুলো রেকর্ড আনবে (ডিফল্ট ৫০)
// ---------------------------------------------------------------------------
const RelatedTable = ({
  resource, filter, columns, clickable, linkTo = 'show',
  rowFilter, emptyText, perPage = 50,
}) => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    dataProvider
      .getList(resource, {
        pagination: { page: 1, perPage },
        sort: { field: 'created_at', order: 'DESC' },
        filter,
      })
      .then(({ data }) => {
        if (active) {
          setRows(rowFilter ? data.filter(rowFilter) : data);
          setLoading(false);
        }
      })
      .catch(() => { if (active) { setRows([]); setLoading(false); } });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, JSON.stringify(filter)]);

  if (loading) return <Box p={3} textAlign="center"><CircularProgress size={24} /></Box>;
  if (!rows || rows.length === 0) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">{emptyText || 'কোনো তথ্য পাওয়া যায়নি।'}</Typography>
      </Box>
    );
  }

  // ক্লিক করলে সেই record এর Show বা Edit পেজে নিয়ে যাবে — resource ভেদে
  // কোনটা registered আছে সেটা linkTo দিয়ে ঠিক করে দিতে হবে (deposits/withdrawals
  // এ শুধু Edit আছে, Show নেই)
  const goTo = (row) => {
    if (!clickable) return;
    navigate(linkTo === 'edit' ? `/${resource}/${row.id}` : `/${resource}/${row.id}/show`);
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>{columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.id}
            hover={!!clickable}
            onClick={clickable ? () => goTo(row) : undefined}
            sx={clickable ? { cursor: 'pointer' } : undefined}
          >
            {columns.map((c) => <TableCell key={c.key}>{c.render ? c.render(row) : row[c.key]}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// ---------------------------------------------------------------------------
// backend snake_case ফিল্ড ম্যাপিং — ChatMessagesList.js এর সাথে হুবহু মিলিয়ে
// ---------------------------------------------------------------------------
const CHAT_FIELD = {
  id: 'id',
  user: 'user',
  text: 'text',
  isFromStaff: 'is_from_staff',
  isRead: 'is_read',
  createdAt: 'created_at',
};
const CHAT_RESOURCE = 'chat-messages';

// WebSocket base URL — নিজের env অনুযায়ী REACT_APP_CHAT_WS_URL সেট করুন,
// backend API root — authProvider.js / dataProvider.js এর apiUrl এর সাথে হুবহু মিলিয়ে রাখুন
// (frontend আর backend আলাদা পোর্টে চলে, তাই window.location.host ব্যবহার করা যাবে না)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ckwin44.com';
const CHAT_WS_BASE_URL =
  process.env.REACT_APP_CHAT_WS_URL || API_BASE_URL.replace(/^http/, 'ws');

const CHAT_WS_RECONNECT_BASE_MS = 1000; // প্রথম রিকানেক্ট ডিলে
const CHAT_WS_RECONNECT_MAX_MS = 15000; // ম্যাক্সিমাম ব্যাকঅফ ক্যাপ

function formatChatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// --- একজন নির্দিষ্ট ইউজারের সাথে বাবল-চ্যাট থ্রেড ---
// আসল backend ChatConsumer (ws/chat/<user_id>/) এর protocol অনুযায়ী:
//   কানেক্ট করলেই সার্ভার {"type": "history", "messages": [...]} পাঠায়
//   নতুন মেসেজ আসে {"type": "message", "message": {...}} আকারে
//   পাঠাতে শুধু {"text": "..."} পাঠালেই হয় — is_from_staff সার্ভার নিজেই বসায়
//   auth session/cookie ভিত্তিক (AuthMiddlewareStack) — token query param লাগে না
//   এই consumer-এ mark_read সাপোর্ট নেই, তাই read মার্ক করা এখনো REST দিয়েই হয়
const UserChatThread = ({ userId, avatarUrl, username }) => {
  const dataProvider = useDataProvider();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);

  // --- WebSocket কানেকশন + auto-reconnect (exponential backoff) ---
  // history REST দিয়ে আলাদাভাবে আনার দরকার নেই — কানেক্ট হলেই সার্ভার history পাঠিয়ে দেয়
  const connectWs = useCallback(() => {
    const url = `${CHAT_WS_BASE_URL}/ws/chat/${userId}/`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
      if (mountedRef.current) setConnected(true);
    };

    ws.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }

      // কানেক্ট হওয়ার সাথে সাথেই সার্ভার পুরো history পাঠায় — সেটাই initial state
      if (payload.type === 'history' && Array.isArray(payload.messages)) {
        if (mountedRef.current) {
          setMessages(payload.messages);
          setLoading(false);
        }
        return;
      }

      // নতুন মেসেজ এলে (নিজের পাঠানোটাও broadcast হয়ে ফেরত আসবে)
      if (payload.type === 'message' && payload.message) {
        const incoming = payload.message;
        setMessages((prev) => {
          if (prev.some((m) => m[CHAT_FIELD.id] === incoming[CHAT_FIELD.id])) return prev;
          return [...prev, incoming];
        });
      }
    };

    ws.onerror = (err) => {
      console.error('chat websocket error:', err);
    };

    ws.onclose = () => {
      if (mountedRef.current) setConnected(false);
      if (!mountedRef.current) return;
      const attempt = reconnectAttemptRef.current + 1;
      reconnectAttemptRef.current = attempt;
      const delay = Math.min(CHAT_WS_RECONNECT_BASE_MS * 2 ** attempt, CHAT_WS_RECONNECT_MAX_MS);
      reconnectTimerRef.current = setTimeout(connectWs, delay);
    };
  }, [userId]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    connectWs();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
    };
  }, [connectWs]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // --- অপঠিত (ইউজারের পাঠানো) মেসেজ ওপেন করার সাথে সাথে read মার্ক করা ---
  // ChatConsumer-এ mark_read নেই, তাই সবসময় REST দিয়েই করতে হচ্ছে
  useEffect(() => {
    const unread = messages.filter((m) => !m[CHAT_FIELD.isFromStaff] && !m[CHAT_FIELD.isRead]);
    if (unread.length === 0) return;
    const ids = unread.map((m) => m[CHAT_FIELD.id]);

    Promise.all(
      unread.map((m) =>
        dataProvider.update(CHAT_RESOURCE, {
          id: m[CHAT_FIELD.id],
          data: { [CHAT_FIELD.isRead]: true },
          previousData: m,
        })
      )
    )
      .then(() => {
        setMessages((prev) =>
          prev.map((m) => (ids.includes(m[CHAT_FIELD.id]) ? { ...m, [CHAT_FIELD.isRead]: true } : m))
        );
      })
      .catch((err) => console.error('mark as read error:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const handleSend = () => {
    const text = replyText.trim();
    if (!text || sending) return;

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      setSending(true);
      wsRef.current.send(JSON.stringify({ text }));
      // সার্ভার broadcast করলে onmessage-এ নিজের পাঠানো মেসেজও automatically চলে আসবে
      setReplyText('');
      setSending(false);
    } else {
      // WS ডাউন থাকলে fallback হিসেবে REST দিয়ে পাঠানো
      setSending(true);
      dataProvider
        .create(CHAT_RESOURCE, {
          data: {
            [CHAT_FIELD.user]: userId,
            [CHAT_FIELD.text]: text,
            [CHAT_FIELD.isFromStaff]: true,
          },
        })
        .then(({ data: created }) => {
          setMessages((prev) => [...prev, created]);
          setReplyText('');
        })
        .catch((err) => {
          console.error('send reply error:', err);
          alert('মেসেজ পাঠাতে সমস্যা হয়েছে।');
        })
        .finally(() => setSending(false));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) return <Box p={3} textAlign="center"><CircularProgress size={24} /></Box>;

  return (
    <Paper elevation={0} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: 500, borderRadius: 3 }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar src={avatarUrl || undefined} sx={{ width: 36, height: 36 }} />
        <Typography variant="subtitle2">{username}</Typography>
        <Chip
          size="small"
          label={connected ? 'Live' : 'সংযোগ বিচ্ছিন্ন — reconnecting...'}
          color={connected ? 'success' : 'default'}
          sx={{ ml: 'auto' }}
        />
      </Box>

      <Box
        sx={{
          flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1,
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
        }}
      >
        {messages.length === 0 && (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center" color="text.secondary">
            <Typography variant="body2">কোনো চ্যাট মেসেজ নেই</Typography>
          </Box>
        )}
        {messages.map((m) => {
          const fromStaff = !!m[CHAT_FIELD.isFromStaff];
          return (
            <Box key={m[CHAT_FIELD.id]} sx={{ alignSelf: fromStaff ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
              <Paper
                elevation={0}
                sx={{
                  px: 1.5, py: 1,
                  bgcolor: fromStaff ? 'primary.main' : 'background.paper',
                  color: fromStaff ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  borderTopRightRadius: fromStaff ? 4 : 16,
                  borderTopLeftRadius: fromStaff ? 16 : 4,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m[CHAT_FIELD.text]}</Typography>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: fromStaff ? 'right' : 'left', mt: 0.3 }}>
                {formatChatTime(m[CHAT_FIELD.createdAt])}
              </Typography>
            </Box>
          );
        })}
        <div ref={bottomRef} />
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          placeholder="রিপ্লাই লিখুন..."
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <IconButton color="primary" onClick={handleSend} disabled={sending || !replyText.trim()}>
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Withdraw Check টেবিল
// এই ইউজার যে যে wallet number দিয়ে উইথড্র করেছে, সেই একই নাম্বার দিয়ে
// অন্য কোনো একাউন্ট উইথড্র করে থাকলে সেই উইথড্রগুলোর ডেটা এখানে দেখায়।
// Username এ ক্লিক করলে ওই ইউজারের প্রোফাইল (UserShow) খুলবে।
//   ধাপ ১: এই ইউজারের উইথড্রগুলো এনে unique wallet number এর লিস্ট বানানো
//   ধাপ ২: ওই নাম্বারগুলো দিয়ে সব উইথড্র এনে নিজের (এই ইউজারের) গুলো বাদ দেওয়া
// ---------------------------------------------------------------------------
const WithdrawCheckTable = ({ userId }) => {
  const dataProvider = useDataProvider();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    dataProvider
      .getList('withdrawals', {
        pagination: { page: 1, perPage: 200 },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { user: userId },
      })
      .then(({ data }) => {
        const numbers = [...new Set(data.map((w) => w.wallet_number).filter(Boolean))];
        if (numbers.length === 0) return [];
        return dataProvider
          .getList('withdrawals', {
            pagination: { page: 1, perPage: 500 },
            sort: { field: 'created_at', order: 'DESC' },
            filter: { wallet_number: numbers },
          })
          .then(({ data: all }) =>
            all
              .filter((w) => String(w.user) !== String(userId))
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          );
      })
      .then((list) => { if (active) { setRows(list); setLoading(false); } })
      .catch(() => { if (active) { setRows([]); setLoading(false); } });

    return () => { active = false; };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Box p={3} textAlign="center"><CircularProgress size={24} /></Box>;

  if (!rows || rows.length === 0) {
    return (
      <Box p={3}>
        <Typography color="text.secondary">
          এই ইউজারের ওয়ালেট নাম্বার অন্য কোনো একাউন্ট ব্যবহার করেনি।
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        এই ইউজারের ওয়ালেট নাম্বার ব্যবহার করে অন্য একাউন্টগুলো যেসব উইথড্র করেছে ({rows.length} টি)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Id</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>Wallet Number</TableCell>
            <TableCell>Method</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.id}</TableCell>
              <TableCell>
                <RouterLink
                  to={`/users/${r.user}/show`}
                  style={{ color: '#2ecc71', textDecoration: 'underline' }}
                >
                  {r.username}
                </RouterLink>
              </TableCell>
              <TableCell>{r.wallet_number}</TableCell>
              <TableCell>{r.method_name}</TableCell>
              <TableCell>{fmt(r.amount_bdt)}</TableCell>
              <TableCell><StatusChip status={r.status} /></TableCell>
              <TableCell>{fmtDate(r.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default function UserShow() {
  const { id } = useParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const { data: user, isLoading: userLoading, refetch: refetchUser } = useGetOne('users', { id });
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [permLoading, setPermLoading] = useState(false);

  // --- নতুন: উইথড্র লিমিট এডিট ডায়ালগের জন্য state ---
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [limitValue, setLimitValue] = useState('');
  const [limitLoading, setLimitLoading] = useState(false);

  const loadSummary = () => {
    setSummaryLoading(true);
    dataProvider
      .getUserSummary(id)
      .then((data) => setSummary(data))
      .catch(() => notify('সামারি লোড করা যায়নি', { type: 'error' }))
      .finally(() => setSummaryLoading(false));
  };

  useEffect(() => { loadSummary(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlock = () => {
    setActionLoading(true);
    dataProvider
      .blockUser(id, reasonText)
      .then(() => {
        notify('ইউজার ব্লক করা হয়েছে', { type: 'success' });
        setBlockDialogOpen(false);
        setReasonText('');
        refetchUser();
        loadSummary();
      })
      .catch(() => notify('ব্লক করা যায়নি', { type: 'error' }))
      .finally(() => setActionLoading(false));
  };

  const handleUnblock = () => {
    setActionLoading(true);
    dataProvider
      .unblockUser(id)
      .then(() => {
        notify('ইউজার আনব্লক করা হয়েছে', { type: 'success' });
        refetchUser();
        loadSummary();
      })
      .catch(() => notify('আনব্লক করা যায়নি', { type: 'error' }))
      .finally(() => setActionLoading(false));
  };

  const handleAddNote = () => {
    setActionLoading(true);
    dataProvider
      .addUserNote(id, noteText)
      .then(() => {
        notify('নোট সেভ হয়েছে', { type: 'success' });
        setNoteDialogOpen(false);
        loadSummary();
      })
      .catch(() => notify('নোট সেভ করা যায়নি', { type: 'error' }))
      .finally(() => setActionLoading(false));
  };

  const handlePermissionToggle = (field, value) => {
    setPermLoading(true);
    dataProvider
      .update('users', {
        id,
        data: { [field]: value },
        previousData: user,
      })
      .then(() => {
        notify('অ্যাকাউন্ট পারমিশন আপডেট হয়েছে', { type: 'success' });
        refetchUser();
      })
      .catch(() => notify('আপডেট করা যায়নি (আপনি কি Superuser দিয়ে লগইন করেছেন?)', { type: 'error' }))
      .finally(() => setPermLoading(false));
  };

  // --- নতুন: উইথড্র লিমিট সেভ করার হ্যান্ডলার ---
  // dataProvider.setWithdrawLimit() ব্যবহার করে backend-এর
  // /users/<id>/set_withdraw_limit/ action কল করে, তারপর summary রিফ্রেশ করে
  const handleUpdateLimit = () => {
    const newLimit = Number(limitValue);
    if (isNaN(newLimit) || newLimit < 0) {
      notify('সঠিক অ্যামাউন্ট দিন', { type: 'error' });
      return;
    }
    setLimitLoading(true);
    dataProvider
      .setWithdrawLimit(id, newLimit)
      .then(() => {
        notify('উইথড্র লিমিট আপডেট হয়েছে', { type: 'success' });
        setLimitDialogOpen(false);
        loadSummary();
      })
      .catch(() => notify('লিমিট আপডেট করা যায়নি', { type: 'error' }))
      .finally(() => setLimitLoading(false));
  };

  if (userLoading || summaryLoading || !user || !summary) {
    return <Box p={5} textAlign="center"><CircularProgress /></Box>;
  }

  const isActive = user.is_active;

  // --- Withdrawal Limit কার্ডের জন্য Total / Used / Available হিসাব ---
  const withdrawLimitTotal = Number(summary.withdraw_limit_total || 0);
  const withdrawUsedAmount = Number(summary.withdraw_used_amount || 0);
  const withdrawAvailableLimit = Math.max(withdrawLimitTotal - withdrawUsedAmount, 0);
  const withdrawUsedPercent =
    withdrawLimitTotal > 0 ? ((withdrawUsedAmount / withdrawLimitTotal) * 100).toFixed(1) : '0.0';

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      {/* ---------- হেডার কার্ড ---------- */}
      <Card sx={{ ...CARD_SX, height: 'auto', mb: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2.5, md: 3 }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            {/* অ্যাভাটার + নাম */}
            <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, flexShrink: 0 }}>
              <Avatar src={summary.avatar_url} sx={{ width: 64, height: 64, border: '2px solid', borderColor: isActive ? 'success.main' : 'error.main', flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h6" noWrap sx={{ maxWidth: 220 }}>{user.username}</Typography>
                  <Chip size="small" label={isActive ? 'Active' : 'Blocked'} color={isActive ? 'success' : 'error'} />
                </Box>
                <Typography variant="body2" color="text.secondary">{summary.nickname || '—'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                  UID: {summary.unique_id}
                  <Tooltip title="কপি করুন">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(summary.unique_id)}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: CARD_BORDER }} />

            {/* মেটা তথ্য */}
            <Box
              sx={{
                minWidth: 0, width: { xs: '100%', md: 'auto' },
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, minmax(110px, max-content))' },
                columnGap: { xs: 1.5, md: 4 },
                rowGap: 1,
              }}
            >
              <Box><Typography variant="caption" color="text.secondary">Registration Date</Typography><Typography variant="body2" noWrap>{fmtDate(user.date_joined)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Last Login</Typography><Typography variant="body2" noWrap>{fmtDate(user.last_login)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Email</Typography><Typography variant="body2" noWrap>{user.email || '—'}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">User ID</Typography><Typography variant="body2">{user.id}</Typography></Box>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: CARD_BORDER }} />

            {/* অ্যাকশন বাটন — ডেস্কটপে ডানপ্রান্তে ঠেলে দেওয়া */}
            <Box sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0, ml: { xs: 0, md: 'auto' } }}>
              <Typography variant="caption" color="text.secondary">Account Status</Typography>
              <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                {isActive ? (
                  <Button variant="contained" color="error" startIcon={<BlockIcon />} onClick={() => setBlockDialogOpen(true)} sx={{ borderRadius: 2 }}>
                    Block User
                  </Button>
                ) : (
                  <Button variant="contained" color="success" startIcon={<LockOpenIcon />} onClick={handleUnblock} disabled={actionLoading} sx={{ borderRadius: 2 }}>
                    Unblock User
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<NoteAddIcon />}
                  onClick={() => { setNoteText(summary.block_reason || ''); setNoteDialogOpen(true); }}
                  sx={{ borderRadius: 2 }}
                >
                  Add Note
                </Button>
              </Box>
              {summary.block_reason ? (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, maxWidth: 260 }}>
                  বর্তমান নোট/কারণ: {summary.block_reason}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ---------- স্ট্যাট কার্ড ---------- */}
      <Grid container spacing={{ xs: 1.25, sm: 2 }} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}><StatCard label="Current Balance" value={fmt(summary.current_balance)} color="#16a34a" icon={AccountBalanceWalletIcon} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard label="Total Deposit" value={fmt(summary.total_deposit)} color="#2563eb" icon={PaymentsIcon} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard label="Total Withdrawal" value={fmt(summary.total_withdrawal)} color="#d97706" icon={LocalAtmIcon} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard label="Total Bets" value={fmt(summary.total_bet_amount)} color="#7c3aed" icon={CasinoIcon} /></Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard
            label="Win / Loss"
            value={fmt(summary.profit_loss)}
            color={summary.profit_loss >= 0 ? '#16a34a' : '#dc2626'}
            icon={EmojiEventsIcon}
            sub={summary.profit_loss >= 0 ? 'Profit' : 'Loss'}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}><StatCard label="Referral Earnings" value={fmt(summary.referral_earning)} color="#0891b2" icon={GroupsIcon} /></Grid>
      </Grid>

      {/* ---------- ট্যাব ---------- */}
      <Card sx={CARD_SX}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ px: 1 }}>
          <Tab label="Overview" value="overview" />
          <Tab label="Deposits" value="deposits" />
          <Tab label="Withdrawals" value="withdrawals" />
          {/* নতুন: Withdraw Check — এই ইউজারের ওয়ালেট নাম্বার অন্য কোন কোন একাউন্ট ব্যবহার করেছে */}
          <Tab label="Withdraw Check" value="withdrawcheck" />
          <Tab label="Bets" value="bets" />
          <Tab label="Referrals" value="referrals" />
          <Tab label="Bonuses" value="bonuses" />
          <Tab label="Support" value="support" />
          <Tab label="Live Chat" value="livechat" />
        </Tabs>
        <Divider />

        <Box p={2}>
          {tab === 'overview' && (
            <Grid container spacing={2}>
              {/* সারি ১ */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={PersonIcon} label="Personal Information" color="#3b82f6" />
                    <InfoRow label="User ID" value={user.id} />
                    <InfoRow label="Username" value={user.username} />
                    <InfoRow label="Email" value={user.email || '—'} />
                    <InfoRow label="Account Status" value={<Chip size="small" label={isActive ? 'Active' : 'Blocked'} color={isActive ? 'success' : 'error'} />} />
                    <InfoRow label="Registration Date" value={fmtDate(user.date_joined)} />
                    <InfoRow label="Last Login" value={fmtDate(user.last_login)} last />

                    <Box mt={2}>
                      <SectionHeader icon={TuneIcon} label="Account Permissions" color="#64748b" />
                      <Box display="flex" flexDirection="column">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!user.is_staff}
                              disabled={permLoading}
                              onChange={(e) => handlePermissionToggle('is_staff', e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Is Staff (Django Admin এক্সেস)</Typography>}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!user.is_superuser}
                              disabled={permLoading}
                              onChange={(e) => handlePermissionToggle('is_superuser', e.target.checked)}
                            />
                          }
                          label={<Typography variant="body2">Is Superuser (সব পারমিশন)</Typography>}
                        />
                      </Box>
                      {permLoading && <CircularProgress size={16} />}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={AccountBalanceWalletIcon} label="Wallet & Financial Summary" color="#16a34a" />
                    <InfoRow label="Current Balance" value={fmt(summary.current_balance)} valueColor="success.main" />
                    <InfoRow label="Total Deposit" value={fmt(summary.total_deposit)} />
                    <InfoRow label="Total Withdrawal" value={fmt(summary.total_withdrawal)} />
                    <InfoRow label="Pending Deposit" value={fmt(summary.pending_deposit)} valueColor="warning.main" />
                    <InfoRow label="Pending Withdrawal" value={fmt(summary.pending_withdrawal)} valueColor="warning.main" last />

                    {/* --- নতুন: Withdrawal Limit সামারি — Total / Used (%) / Available, রেফারেন্স ছবির মতো --- */}
                    <Box
                      sx={{
                        mt: 2, p: 1.5, borderRadius: 2,
                        bgcolor: alpha('#fff', 0.02),
                        border: `1px solid ${ROW_BORDER}`,
                      }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                        <Typography variant="body2" color="text.secondary">Total Withdraw Limit</Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmt(withdrawLimitTotal)}
                          </Typography>
                          <Tooltip title="লিমিট এডিট করুন">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setLimitValue(summary.withdraw_limit_total ?? '');
                                setLimitDialogOpen(true);
                              }}
                            >
                              <TuneIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mt={1}>
                        <Typography variant="body2" color="text.secondary">Used</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {fmt(withdrawUsedAmount)}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            ({withdrawUsedPercent}%)
                          </Typography>
                        </Typography>
                      </Box>

                      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mt={1}>
                        <Typography variant="body2" color="text.secondary">Available Limit</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {fmt(withdrawAvailableLimit)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box mt={2}>
                      <SectionHeader icon={PaymentIcon} label="Payment Methods Used" color="#0ea5e9" />
                      {summary.payment_methods_used?.length ? (
                        summary.payment_methods_used.map((m, i) => (
                          <InfoRow
                            key={m.method__name || 'unknown'}
                            label={m.method__name || 'Unknown'}
                            value={`${m.count} বার`}
                            last={i === summary.payment_methods_used.length - 1}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">কোনো ডিপোজিট নেই</Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ ...CARD_SX, mb: 2 }}>
                  <CardContent>
                    <SectionHeader icon={GroupsIcon} label="Referral / Affiliate" color="#a855f7" />
                    <InfoRow label="Referred Users" value={summary.referred_users} />
                    <InfoRow label="Referral Commission" value={fmt(summary.referral_earning)} valueColor="success.main" last />
                  </CardContent>
                </Card>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={CardGiftcardIcon} label="Bonus Information" color="#ec4899" />
                    <InfoRow label="Claimed Bonuses" value={fmt(summary.claimed_bonus_total)} valueColor="success.main" />
                    <InfoRow label="Bonus Count" value={summary.claimed_bonus_count} last />
                  </CardContent>
                </Card>
              </Grid>

              {/* সারি ২ */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={CasinoIcon} label="Betting Activity" color="#f59e0b" />
                    <InfoRow label="Total Bets" value={summary.total_bets} />
                    <InfoRow label="Total Bet Amount" value={fmt(summary.total_bet_amount)} />
                    <InfoRow label="Total Win" value={fmt(summary.total_win)} valueColor="success.main" />
                    <InfoRow label="Total Loss" value={fmt(summary.total_loss)} valueColor="error.main" />
                    <InfoRow
                      label="Profit / Loss"
                      value={fmt(summary.profit_loss)}
                      valueColor={summary.profit_loss >= 0 ? 'success.main' : 'error.main'}
                      last={!summary.top_games?.length}
                    />
                    {summary.top_games?.length ? (
                      <Box mt={1.5}>
                        <Typography variant="caption" color="text.secondary">Top Game/Sport</Typography>
                        {summary.top_games.map((g, i) => (
                          <InfoRow key={g.game__name} label={g.game__name} value={fmt(g.total)} last={i === summary.top_games.length - 1} />
                        ))}
                      </Box>
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={ReceiptLongIcon} label="Recent Transactions" color="#ef4444" />
                    {summary.recent_transactions?.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow><TableCell>Type</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell></TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.recent_transactions.map((t, i) => (
                            <TableRow key={i}>
                              <TableCell>{t.type}</TableCell>
                              <TableCell>{fmt(t.amount)}</TableCell>
                              <TableCell><StatusChip status={t.status} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">কোনো ট্রানজেকশন নেই</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={SupportAgentIcon} label="Support & Account Actions" color="#06b6d4" />
                    <InfoRow label="Support Tickets" value={summary.support_ticket_count} />
                    <InfoRow label="Block Status" value={<Chip size="small" label={isActive ? 'Not Blocked' : 'Blocked'} color={isActive ? 'success' : 'error'} />} />
                    <InfoRow label="Block Reason" value={summary.block_reason || '—'} last />
                  </CardContent>
                </Card>
              </Grid>

              {/* সারি ৩: Recent Bets */}
              <Grid item xs={12}>
                <Card sx={CARD_SX}>
                  <CardContent>
                    <SectionHeader icon={ListAltIcon} label="Recent Bets" color="#8b5cf6" />
                    {summary.recent_bets?.length ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Game</TableCell><TableCell>Bet Amount</TableCell>
                            <TableCell>Multiplier</TableCell><TableCell>Win/Loss</TableCell>
                            <TableCell>Status</TableCell><TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.recent_bets.map((b, i) => (
                            <TableRow key={i}>
                              <TableCell>{b.game}</TableCell>
                              <TableCell>{fmt(b.amount)}</TableCell>
                              <TableCell>{b.multiplier ? `${b.multiplier}x` : '—'}</TableCell>
                              <TableCell sx={{ color: b.win_amount > 0 ? 'success.main' : 'error.main' }}>
                                {b.win_amount > 0 ? `+${fmt(b.win_amount)}` : `-${fmt(b.amount)}`}
                              </TableCell>
                              <TableCell><WinLossChip status={b.status} /></TableCell>
                              <TableCell>{fmtDate(b.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary">কোনো বেট নেই</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tab === 'deposits' && (
            <RelatedTable
              resource="deposits"
              filter={{ user: id }}
              clickable
              linkTo="edit"
              columns={[
                { key: 'order_id', label: 'Order ID' },
                { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount) },
                { key: 'method_name', label: 'Method' },
                { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
                { key: 'created_at', label: 'Date', render: (r) => fmtDate(r.created_at) },
              ]}
            />
          )}

          {tab === 'withdrawals' && (
            <RelatedTable
              resource="withdrawals"
              filter={{ user: id }}
              clickable
              linkTo="edit"
              columns={[
                { key: 'order_id', label: 'Order ID' },
                { key: 'amount_bdt', label: 'Amount', render: (r) => fmt(r.amount_bdt) },
                { key: 'method_name', label: 'Method' },
                { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
                { key: 'created_at', label: 'Date', render: (r) => fmtDate(r.created_at) },
              ]}
            />
          )}

          {/* --- নতুন: Withdraw Check ---
              অন্য কোনো একাউন্ট এই ইউজারের ওয়ালেট নাম্বার ব্যবহার করে উইথড্র করলে
              তাদের ডেটা (Id, Username, Wallet, Method, Amount, Status, Date) এখানে আসে।
              Username এ ক্লিক করলে সেই ইউজারের প্রোফাইল খোলে। --- */}
          {tab === 'withdrawcheck' && <WithdrawCheckTable userId={id} />}

          {tab === 'bets' && (
            <RelatedTable
              resource="bets"
              filter={{ user: id }}
              columns={[
                { key: 'game_name', label: 'Game' },
                { key: 'amount', label: 'Bet Amount', render: (r) => fmt(r.amount) },
                { key: 'multiplier', label: 'Multiplier', render: (r) => (r.multiplier ? `${r.multiplier}x` : '—') },
                { key: 'win_amount', label: 'Win Amount', render: (r) => fmt(r.win_amount) },
                {
                  key: 'status', label: 'Status',
                  render: (r) => <WinLossChip status={Number(r.win_amount) > 0 ? 'Won' : 'Lost'} />,
                },
                { key: 'created_at', label: 'Date', render: (r) => fmtDate(r.created_at) },
              ]}
            />
          )}

          {tab === 'referrals' && (
            <RelatedTable
              resource="referral-earnings"
              filter={{ referrer: id }}
              columns={[
                { key: 'amount', label: 'Commission', render: (r) => fmt(r.amount) },
                { key: 'created_at', label: 'Date', render: (r) => fmtDate(r.created_at) },
              ]}
            />
          )}

          {tab === 'bonuses' && (
            <RelatedTable
              resource="reward-bonus-claims"
              filter={{ user: id }}
              columns={[
                { key: 'amount', label: 'Amount', render: (r) => fmt(r.amount) },
                { key: 'claimed_at', label: 'Claimed At', render: (r) => fmtDate(r.claimed_at) },
              ]}
            />
          )}

          {tab === 'support' && (
            <RelatedTable
              resource="support-tickets"
              filter={{ user: id }}
              clickable
              columns={[
                { key: 'subject', label: 'Subject' },
                { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
                { key: 'created_at', label: 'Date', render: (r) => fmtDate(r.created_at) },
              ]}
            />
          )}

          {/* --- Live Chat: WebSocket-ভিত্তিক রিয়েলটাইম বাবল চ্যাট, polling নেই --- */}
          {tab === 'livechat' && (
            <UserChatThread userId={Number(id)} avatarUrl={summary.avatar_url} username={user.username} />
          )}
        </Box>
      </Card>

      {/* ---------- ব্লক ডায়ালগ ---------- */}
      <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>ইউজারকে ব্লক করুন</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            label="ব্লক করার কারণ (ইউজার লগইন করার সময় এটাই দেখবে)"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained" color="error" onClick={handleBlock} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'Block User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- নোট ডায়ালগ ---------- */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>নোট যোগ করুন</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            label="নোট (এটি DemoWallet.block_reason ফিল্ডে সেভ হবে)"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained" onClick={handleAddNote} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={18} /> : 'সেভ করুন'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------- নতুন: উইথড্র লিমিট এডিট ডায়ালগ ---------- */}
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>উইথড্র লিমিট এডিট করুন</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            type="number"
            fullWidth
            label="নতুন উইথড্র লিমিট (৳)"
            value={limitValue}
            onChange={(e) => setLimitValue(e.target.value)}
            sx={{ mt: 1 }}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained" onClick={handleUpdateLimit} disabled={limitLoading}>
            {limitLoading ? <CircularProgress size={18} /> : 'সেভ করুন'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}